import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { roleLabel, roleColor, isSuperAdmin, isAdmin } from "@/lib/roles";
import { Heart, SignOut, ShieldCheck, UserCircle, Briefcase } from "@phosphor-icons/react";

/**
 * Desktop sidebar (≥ lg breakpoint). Hidden on mobile — current bottom/horizontal nav stays the same.
 * Renders:
 *   - App logo + role badge
 *   - User info (name, UKC)
 *   - Nav items (passed in as `items` prop, with active key)
 *   - Optional `extras` slot at the top for in-context controls (View Toggle is rendered automatically)
 *   - Logout at the bottom
 *
 * Caller wraps page in <div className="min-h-screen bg-background lg:flex"> and offsets main content with lg:pl-64.
 */
export function Sidebar({ items, activeKey, onSelect, accent = "primary", title, footer }) {
  const { user, logout } = useAuth();

  const accentClasses = {
    primary: "bg-primary text-primary-foreground",
    slate: "bg-slate-900 text-white",
  };
  const headerCls = accentClasses[accent] || accentClasses.primary;

  return (
    <aside className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:left-0 lg:w-64 bg-card border-r border-border z-30" data-testid="desktop-sidebar">
      {/* Logo header — matches main header colour */}
      <div className={`px-5 py-4 ${headerCls} flex items-center gap-2`}>
        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
          {accent === "slate"
            ? <ShieldCheck className="w-4 h-4" weight="fill" />
            : <Heart className="w-4 h-4" weight="fill" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading text-lg font-bold tracking-tighter leading-none">Utheri</p>
          <p className="text-[10px] uppercase tracking-wider opacity-80 leading-tight">{title || "Welfare"}</p>
        </div>
      </div>

      {/* User card */}
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center font-bold text-sm uppercase text-secondary-foreground shrink-0">
            {user?.name ? user.name[0] : "U"}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-sm truncate">{user?.name || "Member"}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.memberNumber || user?.ukcNumber || user?.phone || ""}</p>
          </div>
        </div>
        {user?.role && user.role !== "member" && (
          <span className={`inline-block mt-2.5 text-[10px] px-2 py-0.5 rounded-full font-semibold ${
            user.role === "super_admin" ? "bg-amber-500 text-slate-900" :
            user.role === "admin" ? "bg-blue-500 text-white" :
            user.role === "treasurer" ? "bg-emerald-500 text-white" :
            user.role === "secretary" ? "bg-indigo-500 text-white" :
            "bg-slate-200 text-slate-800"
          }`} data-testid="sidebar-role-badge">{roleLabel(user.role)}</span>
        )}
      </div>

      {/* View shortcuts — show only for users with admin-type role (multiple views) */}
      {isAdmin(user) && (
        <div className="px-3 pt-3 pb-1 space-y-1">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold px-2">Views</p>
          <SideLink to="/dashboard" icon={UserCircle} label="Member view" testid="sidebar-link-member" />
          <SideLink to="/admin" icon={Briefcase} label={`${roleLabel(user.role)} view`} testid="sidebar-link-admin" />
          {isSuperAdmin(user) && <SideLink to="/system" icon={ShieldCheck} label="System view" testid="sidebar-link-system" />}
        </div>
      )}

      {/* Page nav items */}
      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1" aria-label="Sidebar navigation">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold px-2 mb-1">{title || "Menu"}</p>
        {items.map((it) => (
          <button
            key={it.key}
            onClick={() => onSelect(it.key)}
            data-testid={`sidebar-nav-${it.key}`}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
              activeKey === it.key
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            }`}
          >
            {it.icon && <it.icon className="w-4 h-4 shrink-0" />}
            <span className="flex-1 text-left truncate">{it.label}</span>
            {typeof it.count === "number" && it.count > 0 && (
              <span className={`text-[10px] rounded-full px-1.5 py-0.5 font-bold ${
                it.key === "pending" ? "bg-destructive text-destructive-foreground" : "bg-primary/15 text-primary"
              }`}>{it.count}</span>
            )}
          </button>
        ))}
      </nav>

      {/* Footer slot + logout */}
      {footer && <div className="px-3 pb-2">{footer}</div>}
      <div className="border-t border-border px-3 py-3">
        <button onClick={logout} data-testid="sidebar-logout-btn" className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
          <SignOut className="w-4 h-4" />
          <span>Sign out</span>
        </button>
      </div>
    </aside>
  );
}

function SideLink({ to, icon: Icon, label, testid }) {
  return (
    <Link to={to} data-testid={testid}>
      <span className="w-full flex items-center gap-3 px-3 py-1.5 rounded-xl text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
        <Icon className="w-4 h-4" />
        <span className="flex-1 truncate">{label}</span>
      </span>
    </Link>
  );
}
